import { VendorSlug } from '../../../shared/vendor/vendor-slug';
import type { CategoryNodeMap } from '../category/category-node-map';
import type { RegionId } from '../regional-sales/region-id';
import type { RegionalSalesFact, RegionalSalesQuery } from '../regional-sales/regional-sales';
import type {
  RegionalSalesReadResult,
  RegionalSalesRepository,
} from '../regional-sales/regional-sales-repository';
import type { SegmentEvaluatorService } from '../segment/segment-evaluator.service';
import type { SkuPredicate } from '../segment/sku-predicate';
import type { EAN } from '../shared/ean';
import type { Measurement, MeasurementFilter } from './measurement';

// ─── Output types ─────────────────────────────────────────────────────────────

/**
 * Per-region volume returned by evaluate() for partitionBy:'region'.
 *
 * One scalar per region rather than a bucket map: the only activated window mode is 'total',
 * so the whole window collapses to a single number and a map of one-entry bucket maps would
 * be ceremony. Keys are branded RegionIds — what the region grain the query named calls a
 * region, never a row key.
 */
export type PartitionedRegionResult = {
  readonly kind: 'partitioned-region';
  readonly partitions: ReadonlyMap<RegionId, number>;
};

/**
 * Per-chain volume returned by evaluate() for partitionBy:'vendor'.
 *
 * The same warmed row the region partition reads, cut the other way: one scalar per chain
 * for the same reason (the only activated window mode is 'total'). Keys are branded
 * VendorSlugs — the chain itself, never the wire's channel-suffixed key, so a consumer that
 * labels a chain in some other vocabulary translates rather than assumes.
 *
 * A chain is a key here whenever the narrowed row holds a cell for it, including one the feed
 * measured at zero: presence answers "does this chain carry it here", which is a different
 * question from "how much".
 */
export type PartitionedVendorResult = {
  readonly kind: 'partitioned-vendor';
  readonly partitions: ReadonlyMap<VendorSlug, number>;
};

// ─── Slot inventories ─────────────────────────────────────────────────────────

/**
 * What both cuts of the map family require of a measurement, in the order the message lists
 * them: total-window volume over an inline universe, cut by a named region grain.
 *
 * Shared because the two partitions answer from ONE warmed row. Every slot below is either a
 * query input — part of the address that row is warmed and read under — or a shape the route
 * cannot express, so a slot either cut accepted alone would address a row the other cannot.
 * What each cut narrows by after the read is its own business, and it appends that itself.
 */
function mapFamilySlots(m: Measurement): string[] {
  const deviating: string[] = [];

  if (m.measure !== 'volume') deviating.push(`measure:${m.measure}`);
  if (m.window?.mode !== 'total') deviating.push(`window.mode:${m.window?.mode ?? 'absent'}`);
  if (m.shareMode !== 'absolute') deviating.push(`shareMode:${m.shareMode}`);
  if (m.grain === 'observations') deviating.push('grain:observations');
  if (m.show !== undefined) deviating.push('show:present');
  if (m.compare !== undefined) deviating.push('compare:present');
  if (m.universe.kind !== 'inline') deviating.push(`universe:${m.universe.kind}`);
  // The feed is in-store only and the read takes no channel narrowing, so accepting a channel
  // filter here would drop it silently (D-03). filter.vendors is the one narrowing served.
  if (m.filter.channels !== undefined) deviating.push('filter.channels:present');
  // Which regions a key names is not guessable: landskap slugs and kommunkoder are different
  // answers to the same question, so an absent region grain is a missing input, not a default.
  // It is a query input on both cuts: it decides which row was warmed, not just what a region
  // key would have meant.
  if (m.regionGrain === undefined) deviating.push('regionGrain:absent');

  return deviating;
}

/**
 * The map family has exactly one consumed combination: total-window volume over an inline
 * universe, cut by a region grain, optionally narrowed to some chains. Returns the slots a
 * measurement deviates in, empty when it matches.
 */
export function regionSlots(m: Measurement): string[] {
  const deviating = mapFamilySlots(m);

  // The region partition already answers per region. A narrowing it does not apply would be
  // dropped silently, reporting the country's volume for one region.
  if (m.filter.regions !== undefined) deviating.push('filter.regions:present');

  return deviating;
}

/**
 * The map family's other consumed combination: the same row, cut per chain, optionally
 * narrowed to some regions after the read. Returns the slots a measurement deviates in, empty
 * when it matches.
 *
 * What it requires is `mapFamilySlots` unchanged; the region narrowing is the only slot the
 * two cuts differ on.
 */
export function vendorSlots(m: Measurement): string[] {
  return mapFamilySlots(m);
}

// ─── Routes ───────────────────────────────────────────────────────────────────

/**
 * Evaluates volume per region over the whole window.
 *
 * ACL boundary: RegionalSalesRepository owns the cache key this answer is stored under, the
 * feed's masked product keys, its channel suffixes and its payload nesting (PRD D-03, HC-02).
 * This route describes what it wants — a window, some chains, some SKUs, a region grain — and
 * never sees, builds or passes a key.
 */
export async function evaluateRegionVolume(
  regional: RegionalSalesRepository,
  segments: SegmentEvaluatorService,
  measurement: Measurement,
  nodeMap: CategoryNodeMap | null
): Promise<PartitionedRegionResult> {
  const query = await regionalQuery(segments, measurement, nodeMap, 'region');
  const read = await regional.readFacts(query);
  return {
    kind: 'partitioned-region',
    partitions: sumUnitsByRegion(warmedFacts(read, query)),
  };
}

/**
 * Evaluates volume per chain over the whole window, optionally narrowed to some regions.
 *
 * The narrowing is applied to the decoded facts, never to the query: the row is addressed by
 * the window, the chains, the codes and the grain, so a narrowed question reads the row its
 * unnarrowed form already warmed. Asking about one region therefore costs one read and no
 * warm job — which is the point, because the consumer asking is a click on a map that has
 * already drawn every region from that same row.
 */
export async function evaluateVendorVolume(
  regional: RegionalSalesRepository,
  segments: SegmentEvaluatorService,
  measurement: Measurement,
  nodeMap: CategoryNodeMap | null
): Promise<PartitionedVendorResult> {
  const query = await regionalQuery(segments, measurement, nodeMap, 'vendor');
  const read = await regional.readFacts(query);
  const facts = narrowFacts(warmedFacts(read, query), measurement.filter);
  return { kind: 'partitioned-vendor', partitions: sumUnitsByVendor(facts) };
}

// ─── Query ────────────────────────────────────────────────────────────────────

// The slot inventories rejected an absent window, an absent region grain and a rollup universe
// before either route was reached, so the assertions below stand for slots that cannot be
// missing here. `partition` names the caller so a drift between dispatch and inventory reports
// which of the two it is.
async function regionalQuery(
  segments: SegmentEvaluatorService,
  measurement: Measurement,
  nodeMap: CategoryNodeMap | null,
  partition: 'region' | 'vendor'
): Promise<RegionalSalesQuery> {
  const { universe } = measurement;
  if (universe.kind !== 'inline') {
    throw new Error(
      `MeasurementEvaluatorService.evaluate: partitionBy:${partition} reached the route with a ` +
        `${universe.kind} universe — the dispatch and the slot inventory have drifted apart`
    );
  }
  if (nodeMap === null) {
    throw new Error(
      `MeasurementEvaluatorService.evaluate: nodeMap is required for partitionBy:${partition} ` +
        '(the universe resolves to EANs through the segment evaluator)'
    );
  }

  // The domain names chains, never the wire's channel-suffixed keys: VendorSlug rejects a
  // suffixed value here rather than letting the ACL key a row that exists nowhere.
  const vendors = (measurement.filter.vendors ?? []).map((vendor) => VendorSlug(vendor));
  const eans = await universeEans(segments, universe.predicate, nodeMap);
  const win = measurement.window!;

  return {
    eans,
    vendors,
    dateStart: win.range.from,
    dateEnd: win.range.to,
    grain: measurement.regionGrain!,
  };
}

/**
 * The codes a universe covers.
 *
 * An `ean-in` universe is answered from the condition itself rather than by asking the
 * catalog which of those codes it still has rows for. Three reasons, in order of weight:
 *
 * 1. Re-resolving cannot make the answer more correct. The list is already the resolved
 *    membership -- the map's loads build it from a universe they resolved themselves -- so
 *    the round trip can only return the same set or a subset of it.
 * 2. A subset is actively wrong here. The row this query reads is addressed by its code
 *    list, and the caller warmed that row for the full list. Dropping a code the catalog
 *    has no row for addresses a row nobody warmed, which reads back as "no sales" for a
 *    brand that has them.
 * 3. The round trip does not scale. The compiled condition becomes one `.or()` in a GET
 *    query string; a category load carries thousands of codes and the URL runs into the
 *    proxy's request-line limit (HTTP 414).
 *
 * Every other universe kind is a formula whose members only the catalog knows, and goes
 * through the evaluator unchanged.
 */
async function universeEans(
  segments: SegmentEvaluatorService,
  predicate: SkuPredicate,
  nodeMap: CategoryNodeMap
): Promise<readonly EAN[]> {
  if (predicate !== true && predicate !== false && predicate.kind === 'ean-in') {
    return predicate.eans;
  }
  return [...(await segments.evaluate(predicate, nodeMap))];
}

// ─── Reading ──────────────────────────────────────────────────────────────────

/**
 * The read's facts, once it is established that the aggregation was actually made.
 *
 * Three reads are not an answer and each throws naming what was asked for (PRD AC-14): no row
 * (nothing has been warmed for these parameters), a row with no payload (the column held
 * something that is not an aggregation), and a payload every cell of which failed to decode —
 * the shape a `{_status, reason}` marker takes once the ACL has walked it. Reporting any of
 * them as an empty map would draw a map of Sweden with zero sales everywhere, which is a
 * measurement the feed never made.
 *
 * A payload that decoded cleanly to nothing is a different thing entirely: the aggregation ran
 * and found no sales. That is an answer, and it comes back as an empty map. A payload where
 * only some cells failed is likewise an answer — the aggregation demonstrably ran — so the
 * survivors are summed and the failures are left alone: the ACL already reported every one of
 * them, and reporting them a second time here would say nothing new.
 */
function warmedFacts(
  read: RegionalSalesReadResult,
  query: RegionalSalesQuery
): readonly RegionalSalesFact[] {
  if (!read.rowExists) {
    throw new Error(
      `MeasurementEvaluatorService.evaluate: no warmed regional row for ${describeQuery(query)}`
    );
  }
  if (!read.hasPayload) {
    throw new Error(
      `MeasurementEvaluatorService.evaluate: the warmed regional row carries no payload for ` +
        `${describeQuery(query)}`
    );
  }

  const facts: RegionalSalesFact[] = [];
  let failures = 0;
  for (const result of read.facts) {
    if (result.kind === 'ok') facts.push(result.fact);
    else failures++;
  }
  if (facts.length === 0 && failures > 0) {
    throw new Error(
      `MeasurementEvaluatorService.evaluate: the warmed regional row decoded to 0 facts and ` +
        `${failures} reported failure(s) for ${describeQuery(query)}`
    );
  }
  return facts;
}

/**
 * Names what was asked for, in the domain's own words. The cache key belongs to the ACL and
 * would tell a reader nothing anyway; the parameters it was derived from are the context that
 * makes the failure actionable. EANs are counted rather than listed — a brand's whole SKU list
 * would bury the rest.
 */
function describeQuery(query: RegionalSalesQuery): string {
  const vendors = query.vendors.length > 0 ? query.vendors.join(', ') : 'every vendor';
  return (
    `region grain ${query.grain}, ${query.dateStart}..${query.dateEnd}, ` +
    `${vendors}, ${query.eans.length} EAN(s)`
  );
}

// ─── Partitioning ─────────────────────────────────────────────────────────────

/**
 * Sums each region's weekly cells into one volume.
 *
 * Every fact in the row is counted: the row is addressed by the window, the chains and the
 * SKUs it was warmed for, so it already is the vendor-filtered answer over that window. A
 * second filter here would not narrow anything, and a week filter would be actively wrong —
 * the feed's first week of a range starting mid-week is a partial week the aggregation
 * legitimately includes, and its Monday falls before the range.
 *
 * Consumers downstream (`regionalSalesTotals`, the region panel) do still filter these facts
 * by chain. That is redundant rather than contradictory: it is the behavior master shipped,
 * it cannot change a number for a row warmed under the same selection, and master parity
 * governs this seam — so it stays until something other than parity decides otherwise.
 */
function sumUnitsByRegion(facts: readonly RegionalSalesFact[]): ReadonlyMap<RegionId, number> {
  const totals = new Map<RegionId, number>();
  for (const fact of facts) {
    totals.set(fact.region, (totals.get(fact.region) ?? 0) + fact.unitsSold);
  }
  return totals;
}

/**
 * The facts the post-read region filter leaves standing.
 *
 * Absent means "every one", not "none": a filter narrows, so a slot the caller never named
 * cannot remove anything.
 */
function narrowFacts(
  facts: readonly RegionalSalesFact[],
  filter: MeasurementFilter
): readonly RegionalSalesFact[] {
  if (filter.regions === undefined) return facts;
  const regions = new Set(filter.regions);
  return facts.filter((fact) => regions.has(fact.region));
}

/**
 * Sums each chain's weekly cells into one volume.
 *
 * Every surviving fact is counted, across regions and SKUs alike: what the caller wanted
 * narrowed it said in the filter, and the row is already the vendor-filtered answer over the
 * window (see `sumUnitsByRegion` for why a second chain filter would change nothing).
 *
 * A chain the narrowed facts mention is always a key, even at zero units, because the feed
 * measuring a cell at zero is a different statement from the feed having no cell at all.
 */
function sumUnitsByVendor(facts: readonly RegionalSalesFact[]): ReadonlyMap<VendorSlug, number> {
  const totals = new Map<VendorSlug, number>();
  for (const fact of facts) {
    const vendor = VendorSlug(fact.vendor);
    totals.set(vendor, (totals.get(vendor) ?? 0) + fact.unitsSold);
  }
  return totals;
}
